import {
  Zap,
  TrendingUp,
  Target,
  Award,
  Heart,
  Shield,
  Lightbulb,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const About = () => {
  const highlights = [
    { text: "20+ years of industry experience", icon: TrendingUp },
    { text: "Multi-million dollar project management", icon: Award },
    { text: "Compliant procurement systems", icon: Shield },
    { text: "Emergency response logistics expertise", icon: Zap },
  ];

  const values = [
    { text: "Integrity & Transparency", icon: Shield },
    { text: "Innovation & Excellence", icon: Lightbulb },
    { text: "Impact & Results", icon: Award },
  ];

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-gradient-to-br from-slate-200 via-white to-indigo-200 py-20"
    >
      <Helmet>
        <title>About Us | Northstart Systems</title>
      </Helmet>

      {/* Soft background accents */}
      <div className="pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="pointer-events-none absolute top-20 right-1/4 h-80 w-80 rounded-full bg-indigo-200/35 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-cyan-200/25 blur-3xl" />

      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.06) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="mx-auto mb-14 max-w-4xl text-center">
          <div className="inline-flex items-center gap-4 rounded-full border border-indigo-200 bg-white/70 px-5 py-2 text-xl font-bold text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm">
              <Heart className="h-4 w-4" />
            </span>
            <span>About Northstart Systems</span>
          </div>

          <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Building digital systems with{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              clarity & care
            </span>
          </h2>

          <p className="mt-4 text-lg leading-relaxed text-slate-900 md:text-xl">
            We support institutions across the globe with reliable ICT,
            security, and digital solutions designed for real workflows and lasting impact.
          </p>
        </div>

        {/* Cards */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Mission Card */}
          <article className="rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                <Target className="h-5 w-5" />
              </span>
              <h3 className="text-2xl font-extrabold text-slate-900">Our Mission</h3>
            </div>

            <p className="mt-4 text-xl leading-relaxed text-slate-900 md:text-lg">
              To provide reliable, cutting-edge ICT and security solutions that empower
              institutions, safeguard communities, and fuel digital transformation across Kenya
              and beyond.
            </p>

            <div className="mt-7">
              <div className="text-xl font-bold text-slate-900">What we’re known for:</div>
              <ul className="mt-4 space-y-3">
                {highlights.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={index} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-base font-semibold text-slate-900">
                        {item.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </article>

          {/* Values Card */}
          <aside className="rounded-3xl bg-gradient-to-br from-blue-200 to-indigo-400 p-8 text-black shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 ring-1 ring-white/90">
                <Shield className="h-5 w-5" />
              </span>
              <h4 className="text-2xl font-extrabold text-indigo-600">Our Core Values</h4>
            </div>

            <p className="mt-4 text-base leading-relaxed text-black font-bold md:text-lg">
              We keep things simple: do the right thing, build with excellence, and deliver
              outcomes you can trust.
            </p>

            <ul className="mt-7 space-y-4">
              {values.map((v, idx) => {
                const Icon = v.icon;
                return (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-base font-semibold">{v.text}</div>
                      <div className="mt-1 text-sm font-bold text-black/80">
                        {idx === 0 && "Honest communication and accountable delivery."}
                        {idx === 1 && "Practical innovation that improves real workflows."}
                        {idx === 2 && "Measurable outcomes, not buzzwords."}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Mini trust row */}
            <div className="mt-8 flex flex-wrap gap-8">
              {["Reliable", "Responsive", "Professional"].map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-6 rounded-full bg-white/55 px-3 py-1 text-xl font-semibold ring-1 ring-indigo-600"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {tag}
                </span>
              ))}
            </div>
          </aside>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/about/details"
            className="group inline-flex items-center justify-center rounded-2xl bg-indigo-600 px-8 py-4 text-base font-bold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700"
          >
            View More About Us
            <ArrowRight className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
          </Link>
          <p className="mt-3 text-xl text-slate-900">
            Want a quick chat? Head to the contact section and we’ll respond fast.
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;